/**
 * FacetFiltersForm - Main filter component for collection pages
 * Handles filter interactions, section rendering API, and panel state management
 * Follows Shopify best practices for section rendering and accessibility
 */
const FILTER_LOCK_OWNED_ATTR = 'data-filter-lock-owned';

function filterOwnsBodyLock() {
  return document.body.hasAttribute(FILTER_LOCK_OWNED_ATTR);
}

function setFilterBodyLockOwned(owned) {
  if (owned) {
    document.body.setAttribute(FILTER_LOCK_OWNED_ATTR, 'true');
  } else {
    document.body.removeAttribute(FILTER_LOCK_OWNED_ATTR);
  }
}

class FacetFiltersForm extends HTMLElement {

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Debounce form submissions to prevent excessive API calls
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // Set up filter listeners (checkboxes only, not price range sliders)
    this.setupFilterListeners();

    const filterButton = document.getElementById('filter-button');
    if (filterButton) {
      filterButton.addEventListener('click', this.toggleFilters.bind(this));
    }
    
    const overlay = document.getElementById('overlay');
    if (overlay) {
      overlay.addEventListener('click', this.closeFilterDrawer);
    }
    
    // Handle sort button
    const sortButton = document.getElementById('sort-button');
    const sortDropdown = document.getElementById('sort-dropdown');
    if (sortButton && sortDropdown) {
      sortButton.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = sortButton.getAttribute('aria-expanded') === 'true';
        sortButton.setAttribute('aria-expanded', !isExpanded);
        sortDropdown.hidden = isExpanded;
      });
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!sortButton.contains(e.target) && !sortDropdown.contains(e.target)) {
          sortButton.setAttribute('aria-expanded', 'false');
          sortDropdown.hidden = true;
        }
      });
      
      // Handle sort option clicks - use section rendering API instead of page reload
      sortDropdown.querySelectorAll('.sort-dropdown__option').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const sortValue = option.getAttribute('data-sort-value');
          
          // Update URL params and use section rendering API
          const currentUrl = new URL(window.location);
          currentUrl.searchParams.set('sort_by', sortValue);
          
          // Get current filter params and add sort_by
          const form = this.querySelector('form');
          if (form) {
            const formData = new FormData(form);
            formData.set('sort_by', sortValue);
            // Preserve search query parameter for search pages
            const searchQuery = currentUrl.searchParams.get('q');
            if (searchQuery) {
              formData.set('q', searchQuery);
              formData.set('options[prefix]', 'last');
            }
            const searchParams = new URLSearchParams(formData).toString();
            FacetFiltersForm.renderPage(searchParams, e);
          } else {
            // Fallback: use URL params directly
            const searchParams = currentUrl.search.substring(1);
            FacetFiltersForm.renderPage(searchParams, e);
          }
          
          // Close dropdown
          sortButton.setAttribute('aria-expanded', 'false');
          sortDropdown.hidden = true;
        });
      });
    }

    const closeButtons = this.querySelectorAll('.filter__close');

    if (closeButtons) {
      closeButtons.forEach(closeButton => {
        closeButton.addEventListener('click', (event) => {
          // Check if this is the Apply button (mobile)
          const isApplyButton = closeButton.classList.contains('button') && 
                                closeButton.textContent.trim().toLowerCase() === 'apply';
          
          if (isApplyButton) {
            // On mobile Apply button: submit form then close drawer
            event.preventDefault();
            const form = this.querySelector('form');
            if (form) {
              const formData = new FormData(form);
              // Preserve search query parameter for search pages
              const currentUrl = new URL(window.location);
              const searchQuery = currentUrl.searchParams.get('q');
              if (searchQuery) {
                formData.set('q', searchQuery);
                formData.set('options[prefix]', 'last');
              }
              const searchParams = new URLSearchParams(formData).toString();
              FacetFiltersForm.renderPage(searchParams, event);
              // Close drawer after submitting
              this.closeFilterDrawer();
            }
          } else {
            // On close button: close drawer or hide panel
            const isDesktop = window.matchMedia('(min-width: 768px)').matches;
            if (isDesktop) {
              this.toggleFilters();
            } else {
              this.closeFilterDrawer();
            }
          }
        });
      });
    }
  }

  /**
   * Set up event listeners for filter inputs
   * Only listens to checkbox and select changes, not price range sliders
   * Price range submission is handled separately in PriceRange class
   */
  setupFilterListeners() {
    const form = this.querySelector('form');
    if (!form) {
      console.warn('FacetFiltersForm: No form element found');
      return;
    }

    // Add change listeners to checkbox inputs only (not price range sliders)
    form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', this.debouncedOnSubmit.bind(this));
    });

    // Handle sort select changes
    const sortSelect = form.querySelector('select[name="sort_by"]');
    if (sortSelect) {
      sortSelect.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    // Note: Price range inputs submit only when:
    // 1. User clicks Apply button (mobile)
    // 2. User changes number inputs AND values differ from defaults
    // This is handled in the PriceRange class
  }

  /**
   * Toggle filter panel visibility
   * Desktop: Shows/hides inline column
   * Mobile: Opens/closes drawer with overlay
   */
  toggleFilters() {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const filterDrawer = document.getElementById('main-collection-filters');
    const filterButton = document.getElementById('filter-button');
    const buttonText = filterButton?.querySelector('.filter-button-text');
    
    if (!filterDrawer || !filterButton) {
      console.warn('FacetFiltersForm: Filter drawer or button not found');
      return;
    }

    const isExpanded = filterDrawer.getAttribute('aria-expanded') === 'true';

    if (isDesktop) {
      // Desktop: toggle inline column visibility
      const newState = !isExpanded;
      filterDrawer.setAttribute('aria-expanded', newState);
      filterButton.setAttribute('aria-expanded', newState);
      
      // Update button text for accessibility
      if (buttonText) {
        buttonText.textContent = newState ? 'Hide Filters' : 'Show Filters';
      }
    } else {
      // Mobile: toggle drawer (open/close)
      if (!isExpanded) {
        this.openFilterDrawer();
      } else {
        this.closeFilterDrawer();
      }
    }
  }

  openFilterDrawer() {
    if (!filterOwnsBodyLock()) {
      lockBodyScroll();
      setFilterBodyLockOwned(true);
    }
    const overlay = document.getElementById('overlay');
    if (overlay) {
      overlay.classList.add('active');
    }
    const filterDrawer = document.getElementById('main-collection-filters');
    if (filterDrawer) {
      filterDrawer.setAttribute('aria-expanded', true);
      const filterButton = document.getElementById('filter-button');
      if (filterButton) {
        filterButton.setAttribute('aria-expanded', 'true');
      }
      trapFocus(filterDrawer);
      filterDrawer.focus();
    }
  }

  closeFilterDrawer(event) {
    // Don't close if the click originated from a filter remove link
    if (event && event.target) {
      const clickedLink = event.target.closest('a.active-filter-pill, a.filter-btn--clear-all');
      if (clickedLink) {
        return; // Don't close the drawer when clicking filter remove links
      }
    }
    
    if (filterOwnsBodyLock()) {
      unlockBodyScroll();
      setFilterBodyLockOwned(false);
    }
    const filterDrawer = document.getElementById('main-collection-filters');
    if (filterDrawer) {
      filterDrawer.setAttribute('aria-expanded', false);
    }
    const overlay = document.getElementById('overlay');
    if (overlay) {
      overlay.classList.remove('active');
    }
    const filterButton = document.getElementById('filter-button');
    if (filterButton) {
      filterButton.setAttribute('aria-expanded', 'false');
      removeTrapFocus(filterButton);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    // Preserve filter panel state before rendering
    const filterDrawer = document.getElementById('main-collection-filters');
    const filterButton = document.getElementById('filter-button');
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    
    // Store complete state
    const panelState = {
      wasExpanded: filterDrawer?.getAttribute('aria-expanded') === 'true',
      wasButtonExpanded: filterButton?.getAttribute('aria-expanded') === 'true',
      buttonText: filterButton?.querySelector('.filter-button-text')?.textContent || '',
      hadOverlay: document.getElementById('overlay')?.classList.contains('active') || false,
      hadBodyOverflow: document.body.classList.contains('overflow-hidden') || false,
      isDesktop: isDesktop
    };
    
    // Preserve page parameter if not changing filters (for sorting only)
    const currentUrl = new URL(window.location);
    const currentPage = currentUrl.searchParams.get('page');
    const isFilterChange = event && (event.target.closest('.js-filter') || event.target.closest('price-range'));
    
    // If only sorting (not filtering), preserve page parameter
    if (!isFilterChange && currentPage && searchParams) {
      const params = new URLSearchParams(searchParams);
      if (!params.has('page')) {
        params.set('page', currentPage);
        searchParams = params.toString();
      }
    }
    
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collectionElement = productGridContainer.querySelector('.collection');
      if (collectionElement) {
        collectionElement.classList.add('loading');
      }
    }

    sections.forEach((section) => {
      // Construct URL with section_id and search params
      const pathname = window.location.pathname;
      const url = searchParams 
        ? `${pathname}?section_id=${section.section}&${searchParams}`
        : `${pathname}?section_id=${section.section}`;
      const filterDataUrl = element => element.url === url;
      const usingCache = FacetFiltersForm.filterData.some(filterDataUrl);

      usingCache ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    
    // Restore filter panel state after rendering completes
    // Use double requestAnimationFrame to ensure all DOM updates are complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        FacetFiltersForm.restorePanelState(panelState);
        // Re-initialize pagination if it exists
        const pagination = document.querySelector('pagination-hybrid');
        if (pagination && pagination.initFromURL) {
          pagination.initFromURL();
        }
      });
    });
  }
  
  static restorePanelState(state) {
    const filterDrawer = document.getElementById('main-collection-filters');
    const filterButton = document.getElementById('filter-button');
    
    if (!filterDrawer || !filterButton) return;
    
    // Always restore the panel state if it was expanded
    if (state.wasExpanded) {
      filterDrawer.setAttribute('aria-expanded', 'true');
      filterButton.setAttribute('aria-expanded', 'true');
      
      if (state.isDesktop) {
        // Desktop: restore button text
        const buttonText = filterButton.querySelector('.filter-button-text');
        if (buttonText && state.buttonText) {
          buttonText.textContent = state.buttonText;
        } else if (buttonText) {
          buttonText.textContent = 'Hide Filters';
        }
      } else {
        // Mobile: restore overlay and body overflow
        if (state.hadOverlay) {
          const overlay = document.getElementById('overlay');
          if (overlay) {
            overlay.classList.add('active');
          }
        }
        if (state.hadBodyOverflow) {
          if (!document.body.classList.contains('overflow-hidden') && !filterOwnsBodyLock()) {
            lockBodyScroll();
            setFilterBodyLockOwned(true);
          }
        }
      }
    }
  }

  /**
   * Fetch section HTML from Shopify section rendering API
   * @param {string} url - URL with section_id parameter
   * @param {Event} event - Original event that triggered the fetch
   */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        
        // Cache the response for future use
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        
        // Limit cache size to prevent memory issues
        if (FacetFiltersForm.filterData.length > 50) {
          FacetFiltersForm.filterData.shift();
        }
        
        // Render the updated sections
        try {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
        } catch (error) {
          console.error('FacetFiltersForm: Error rendering sections', error);
        }
        
        // Remove loading class after rendering
        FacetFiltersForm.removeLoadingState();
      })
      .catch((error) => {
        console.error('FacetFiltersForm: Error fetching section:', error);
        FacetFiltersForm.removeLoadingState();
        
        // Show user-friendly error message
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) {
          const errorMsg = document.createElement('div');
          errorMsg.className = 'filter-error';
          errorMsg.textContent = 'Unable to load products. Please try again.';
          errorMsg.style.padding = 'var(--m)';
          errorMsg.style.textAlign = 'center';
          errorMsg.style.color = '#999';
          productGridContainer.appendChild(errorMsg);
          
          // Remove error message after 5 seconds
          setTimeout(() => errorMsg.remove(), 5000);
        }
      });
  }
  
  /**
   * Remove loading state from product grid
   */
  static removeLoadingState() {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collectionElement = productGridContainer.querySelector('.collection');
      if (collectionElement) {
        collectionElement.classList.remove('loading');
      }
    }
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cachedData = FacetFiltersForm.filterData.find(filterDataUrl);
    const html = cachedData.html;
    
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    
    // Remove loading class after rendering
    FacetFiltersForm.removeLoadingState();
  }

  /**
   * Render product grid container using section rendering API response
   * @param {string} html - HTML string from section rendering API
   */
  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) {
      console.error('FacetFiltersForm: ProductGridContainer not found');
      return;
    }
    
    try {
      const parsedHTML = parseHTML(html);
      const newContainer = parsedHTML.getElementById('ProductGridContainer');
      if (newContainer) {
        container.innerHTML = newContainer.innerHTML;
      } else {
        console.error('FacetFiltersForm: ProductGridContainer not found in response');
      }
    } catch (error) {
      console.error('FacetFiltersForm: Error parsing HTML', error);
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetElement = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (targetElement) {
        targetElement.innerHTML = element.innerHTML;
        // Sync class list for active filters visibility
        if (element.classList.contains('filter-group--hidden')) {
          targetElement.classList.add('filter-group--hidden');
        } else {
          targetElement.classList.remove('filter-group--hidden');
        }
      }
    });
    
    // Always sync active filters visibility (even if it was the click source)
    const activeFiltersSource = parsedHTML.querySelector('.js-filter[data-index="active"]');
    const activeFiltersTarget = document.querySelector('.js-filter[data-index="active"]');
    if (activeFiltersSource && activeFiltersTarget) {
      // Update innerHTML
      activeFiltersTarget.innerHTML = activeFiltersSource.innerHTML;
      // Sync hidden class
      if (activeFiltersSource.classList.contains('filter-group--hidden')) {
        activeFiltersTarget.classList.add('filter-group--hidden');
      } else {
        activeFiltersTarget.classList.remove('filter-group--hidden');
      }
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    
    // Re-initialize filter listeners after DOM update
    FacetFiltersForm.reinitializeFilterListeners();

    // If a filter group was clicked (for counts update), also check if it was a clear action
    if (countsToRender && event) {
      const targetFilter = event.target.closest('.js-filter');
      if (targetFilter) {
        // Check if the click was on a clear button (filter-btn--clear or filter-btn--clear-all)
        const isClearAction = event.target.closest('.filter-btn--clear, .filter-btn--clear-all');
        
        // If it's a clear action, update the innerHTML to uncheck checkboxes
        // Otherwise, just update the counts to avoid flickering
        if (isClearAction) {
          // Update innerHTML to reflect cleared state (unchecked checkboxes)
          targetFilter.innerHTML = countsToRender.innerHTML;
          // Sync class list for active filters visibility
          if (countsToRender.classList.contains('filter-group--hidden')) {
            targetFilter.classList.add('filter-group--hidden');
          } else {
            targetFilter.classList.remove('filter-group--hidden');
          }
          // Re-initialize listeners for this filter group after updating innerHTML
          const facetForm = document.querySelector('facet-filters-form');
          if (facetForm) {
            facetForm.setupFilterListeners();
          }
        } else {
          // Just update counts for non-clear actions (checkbox clicks)
          FacetFiltersForm.renderCounts(countsToRender, targetFilter);
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-filters__tags'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        // Replace innerHTML - browser will automatically upgrade custom elements
        targetElement.innerHTML = activeFacetsElement.innerHTML;
        
        // Force re-initialization of custom elements after DOM update
        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          targetElement.querySelectorAll('facet-remove').forEach((element) => {
            // Reset initialized flag and trigger connectedCallback
            element.initialized = false;
            if (element.connectedCallback) {
              element.connectedCallback();
            }
          });
        });
      }
    })

    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.updateFilterBadge();
  }

  static updateFilterBadge() {
    const activeFiltersTags = document.querySelector('.active-filters__tags');
    const filterButton = document.getElementById('filter-button');
    
    if (!filterButton) return;

    // Find or create badge element
    let badge = filterButton.querySelector('.filter-sort-button__badge');
    
    if (!activeFiltersTags) {
      // Remove badge if no active filters container
      if (badge) badge.remove();
      return;
    }

    // Count active filter tags (pills)
    const filterTags = activeFiltersTags.querySelectorAll('.active-filter-pill');
    const activeFiltersCount = filterTags.length;

    if (activeFiltersCount > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'filter-sort-button__badge';
        filterButton.appendChild(badge);
      }
      badge.textContent = activeFiltersCount;
    } else {
      // Remove badge if no active filters
      if (badge) badge.remove();
    }
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetEl = document.querySelector(selector);
      if (targetEl) {
        targetEl.innerHTML = html.querySelector(selector).innerHTML;
      }
    });
  }
  
  /**
   * Re-initialize filter listeners after DOM update
   * This ensures checkboxes in newly rendered filter groups work correctly
   */
  static reinitializeFilterListeners() {
    const facetForm = document.querySelector('facet-filters-form');
    if (facetForm) {
      facetForm.setupFilterListeners();
    }
    
    // Re-initialize price range custom elements
    document.querySelectorAll('price-range').forEach((priceRange) => {
      // Trigger re-initialization by calling constructor-like setup
      if (!priceRange.minSlider || !priceRange.maxSlider) {
        priceRange.minSlider = priceRange.querySelector('.filter-price-range__slider--min');
        priceRange.maxSlider = priceRange.querySelector('.filter-price-range__slider--max');
        priceRange.minInput = priceRange.querySelector('.filter-price-range__input--min');
        priceRange.maxInput = priceRange.querySelector('.filter-price-range__input--max');
        priceRange.track = priceRange.querySelector('.filter-price-range__slider-track');
        priceRange.rangeIndicator = priceRange.querySelector('.filter-price-range__slider-indicator');
        
        if (priceRange.minSlider && priceRange.maxSlider) {
          priceRange.initDualRangeInput();
          requestAnimationFrame(() => {
            priceRange.updateRangeIndicator();
          });
        }
      }
    });
  }

  static renderCounts(source, target) {
    if (!target) {
      return; // Guard against null target
    }
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const queryString = searchParams ? `?${searchParams}` : '';
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${queryString}`);
  }

  /**
   * Get section IDs for section rendering API
   * @returns {Array} Array of section objects with section ID
   */
  static getSections() {
    const collectionGrid = document.getElementById('collection-grid');
    if (!collectionGrid || !collectionGrid.dataset.id) {
      console.error('FacetFiltersForm: Collection grid not found or missing data-id');
      return [];
    }
    return [{
      section: collectionGrid.dataset.id
    }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const formData = new FormData(form);
    
    // Check if price filter should be excluded (at default values)
    const priceRange = form.querySelector('price-range');
    if (priceRange) {
      const minSlider = priceRange.querySelector('.filter-price-range__slider--min');
      const maxSlider = priceRange.querySelector('.filter-price-range__slider--max');
      if (minSlider && maxSlider) {
        const currentMin = Number(minSlider.value);
        const currentMax = Number(maxSlider.value);
        const defaultMin = Number(minSlider.min) || 0;
        const defaultMax = Number(maxSlider.max);
        
        // Remove price params if at default values
        if (currentMin === defaultMin && currentMax === defaultMax) {
          formData.delete(minSlider.name);
          formData.delete(maxSlider.name);
          // Also delete the number input values
          const minInput = priceRange.querySelector('.filter-price-range__input--min');
          const maxInput = priceRange.querySelector('.filter-price-range__input--max');
          if (minInput) formData.delete(minInput.name);
          if (maxInput) formData.delete(maxInput.name);
        }
      }
    }
    
    // Preserve search query parameter for search pages
    const currentUrl = new URL(window.location);
    const searchQuery = currentUrl.searchParams.get('q');
    if (searchQuery) {
      formData.set('q', searchQuery);
      formData.set('options[prefix]', 'last');
    }
    
    // Reset to page 1 when filters change (standard behavior)
    // Remove page parameter when filters are applied
    formData.delete('page');
    
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    // Prevent all default navigation behavior
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (event && event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }
    
    FacetFiltersForm.toggleActiveFacets();
    
    // Get the anchor element - handle both real events and synthetic events
    let anchor;
    if (event && event.target) {
      anchor = event.target.closest('a');
    }
    if (!anchor && event && event.currentTarget) {
      anchor = event.currentTarget;
    }
    if (!anchor) {
      // Fallback: try to find anchor in the facet-remove element
      const facetRemove = event?.target?.closest('facet-remove') || 
                         document.querySelector('facet-remove:has(a.active-filter-pill:focus)') ||
                         document.querySelector('facet-remove:has(a.filter-btn--clear-all:focus)');
      if (facetRemove) {
        anchor = facetRemove.querySelector('a');
      }
    }
    
    if (!anchor || !anchor.href) {
      console.error('FacetRemove: Could not find anchor element');
      return;
    }
    
    try {
      // Handle both absolute and relative URLs
      let url;
      if (anchor.href.startsWith('http://') || anchor.href.startsWith('https://')) {
        url = new URL(anchor.href);
      } else {
        url = new URL(anchor.href, window.location.origin);
      }
      
      // Extract search params from the URL
      // Normalize empty string to null for proper handling in renderPage
      let searchParams = url.search ? url.search.substring(1) : null; // Remove leading '?'
      
      // If searchParams is empty string, normalize to null
      if (searchParams === '') {
        searchParams = null;
      }
      
      // Preserve sort_by parameter if it exists in current URL but not in the remove URL
      const currentUrl = new URL(window.location);
      const currentSortBy = currentUrl.searchParams.get('sort_by');
      if (currentSortBy && (!searchParams || !searchParams.includes('sort_by'))) {
        // Add sort_by to searchParams if it's missing
        if (searchParams) {
          searchParams = `${searchParams}&sort_by=${encodeURIComponent(currentSortBy)}`;
        } else {
          searchParams = `sort_by=${encodeURIComponent(currentSortBy)}`;
        }
      }
      
      // Use section rendering API instead of navigation
      FacetFiltersForm.renderPage(searchParams, event);
    } catch (error) {
      console.error('FacetRemove: Error processing filter removal', error);
      // Fallback: try to extract params manually
      const href = anchor.href;
      const queryIndex = href.indexOf('?');
      if (queryIndex !== -1) {
        let searchParams = href.substring(queryIndex + 1);
        // Normalize empty string to null
        if (searchParams === '') {
          searchParams = null;
        }
        
        // Preserve sort_by parameter if it exists in current URL but not in the remove URL
        const currentUrl = new URL(window.location);
        const currentSortBy = currentUrl.searchParams.get('sort_by');
        if (currentSortBy && (!searchParams || !searchParams.includes('sort_by'))) {
          if (searchParams) {
            searchParams = `${searchParams}&sort_by=${encodeURIComponent(currentSortBy)}`;
          } else {
            searchParams = `sort_by=${encodeURIComponent(currentSortBy)}`;
          }
        }
        
        FacetFiltersForm.renderPage(searchParams, event);
      } else {
        // No query params in URL - check if we need to preserve sort_by
        const currentUrl = new URL(window.location);
        const currentSortBy = currentUrl.searchParams.get('sort_by');
        const searchParams = currentSortBy ? `sort_by=${encodeURIComponent(currentSortBy)}` : null;
        FacetFiltersForm.renderPage(searchParams, event);
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

// Initialize desktop filter panel state - must be defined before called
FacetFiltersForm.initDesktopFilterState = function() {
  const isDesktop = window.matchMedia('(min-width: 769px)').matches;
  const filterDrawer = document.getElementById('main-collection-filters');
  const filterButton = document.getElementById('filter-button');
  const buttonText = filterButton?.querySelector('.filter-button-text');
  
  if (filterDrawer && filterButton) {
    if (isDesktop) {
      // Desktop: filters visible by default (inline column)
      filterDrawer.setAttribute('aria-expanded', 'true');
      filterButton.setAttribute('aria-expanded', 'true');
      
      if (buttonText) {
        buttonText.textContent = 'Hide Filters';
      }
      
      // Ensure overlay is not active on desktop
      const overlay = document.getElementById('overlay');
      if (overlay) {
        overlay.classList.remove('active');
      }
      if (filterOwnsBodyLock()) {
        unlockBodyScroll();
        setFilterBodyLockOwned(false);
      }
    } else {
      // Mobile: filters hidden by default (drawer)
      filterDrawer.setAttribute('aria-expanded', 'false');
      filterButton.setAttribute('aria-expanded', 'false');
      
      if (buttonText) {
        buttonText.textContent = 'Show Filters';
      }
    }
  }
  
  // Handle window resize to update state
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      FacetFiltersForm.initDesktopFilterState();
    }, 250);
  });
};

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


// Update filter badge on initial page load and initialize desktop filter state
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    FacetFiltersForm.updateFilterBadge();
    FacetFiltersForm.initDesktopFilterState();
  });
} else {
  FacetFiltersForm.updateFilterBadge();
  FacetFiltersForm.initDesktopFilterState();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.minSlider = this.querySelector('.filter-price-range__slider--min');
    this.maxSlider = this.querySelector('.filter-price-range__slider--max');
    this.minInput = this.querySelector('.filter-price-range__input--min');
    this.maxInput = this.querySelector('.filter-price-range__input--max');
    this.track = this.querySelector('.filter-price-range__slider-track');
    this.rangeIndicator = this.querySelector('.filter-price-range__slider-indicator');
    
    // Store default values to detect if user has changed them
    this.defaultMin = this.minSlider ? Number(this.minSlider.min) : 0;
    this.defaultMax = this.maxSlider ? Number(this.maxSlider.max) : 0;
    this.hasUserInteracted = false;
    
    // Initialize dual-range-input library (with retry if not loaded yet)
    this.initDualRangeInput();
    
    // Set up slider event listeners for number input sync and range indicator
    // Sliders should NOT trigger form submission - only update UI
    if (this.minSlider && this.maxSlider) {
      this.minSlider.addEventListener('input', () => {
        const minValue = Number(this.minSlider.value);
        const maxValue = Number(this.maxSlider.value);
        
        // Enforce constraint: min cannot exceed max
        if (minValue > maxValue) {
          this.minSlider.value = maxValue;
        }
        
        if (this.minInput) {
          this.minInput.value = this.minSlider.value;
          this.updateInputConstraints();
        }
        this.updateRangeIndicator();
        // Don't submit on slider drag - only update UI
      });
      
      this.maxSlider.addEventListener('input', () => {
        const minValue = Number(this.minSlider.value);
        const maxValue = Number(this.maxSlider.value);
        
        // Enforce constraint: max cannot go below min
        if (maxValue < minValue) {
          this.maxSlider.value = minValue;
        }
        
        if (this.maxInput) {
          this.maxInput.value = this.maxSlider.value;
          this.updateInputConstraints();
        }
        this.updateRangeIndicator();
        // Don't submit on slider drag - only update UI
      });
      
      // Submit on slider release (change event)
      this.minSlider.addEventListener('change', () => {
        this.submitIfChanged();
      });
      
      this.maxSlider.addEventListener('change', () => {
        this.submitIfChanged();
      });
      
      // Initial range indicator update (wait for layout)
      requestAnimationFrame(() => {
        this.updateRangeIndicator();
      });
      
      // Update on resize
      if (window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => {
          this.updateRangeIndicator();
        });
        resizeObserver.observe(this.track);
      }
    }
    
    // Number inputs: only submit if values differ from defaults
    if (this.minInput && this.maxInput) {
      // Use 'change' event (fires on blur) instead of 'input' to avoid auto-submit
      this.minInput.addEventListener('change', this.onInputChange.bind(this));
      this.maxInput.addEventListener('change', this.onInputChange.bind(this));
    }
  }

  onInputChange(event) {
    const input = event.currentTarget;
    const value = Number(input.value);
    const min = Number(input.getAttribute('min')) || 0;
    const max = Number(input.getAttribute('max')) || 0;
    
    // Clamp value to valid range
    const clampedValue = Math.max(min, Math.min(max, value));
    input.value = clampedValue;
    
    // Update corresponding slider
    if (input === this.minInput && this.minSlider) {
      const minValue = Number(this.minSlider.value);
      const maxValue = Number(this.maxSlider.value);
      
      // Enforce constraint: min cannot exceed max
      const finalValue = Math.min(clampedValue, maxValue);
      this.minSlider.value = finalValue;
      
      if (this.dualRangeInput) {
        this.dualRangeInput.update('ceil');
      }
    } else if (input === this.maxInput && this.maxSlider) {
      const minValue = Number(this.minSlider.value);
      const maxValue = Number(this.maxSlider.value);
      
      // Enforce constraint: max cannot go below min
      const finalValue = Math.max(clampedValue, minValue);
      this.maxSlider.value = finalValue;
      
      if (this.dualRangeInput) {
        this.dualRangeInput.update('floor');
      }
    }
    
    this.updateInputConstraints();
    this.updateRangeIndicator();
    
    // Check if values differ from defaults before submitting
    const currentMin = Number(this.minSlider?.value) || 0;
    const currentMax = Number(this.maxSlider?.value) || this.defaultMax;
    const isDefault = currentMin === this.defaultMin && currentMax === this.defaultMax;
    
    // Check if there's an active price filter in URL that needs to be cleared
    const currentUrl = new URL(window.location);
    const hasPriceFilterInUrl = currentUrl.searchParams.has('filter.v.price.gte') || 
                                 currentUrl.searchParams.has('filter.v.price.lte');
    
    // Submit if values are not at defaults OR if there's a price filter to clear
    if (!isDefault || hasPriceFilterInUrl) {
      this.hasUserInteracted = true;
      const form = this.closest('form');
      if (form) {
        const formData = new FormData(form);
        // Preserve search query parameter for search pages
        const searchQuery = currentUrl.searchParams.get('q');
        if (searchQuery) {
          formData.set('q', searchQuery);
          formData.set('options[prefix]', 'last');
        }
        const searchParams = new URLSearchParams(formData).toString();
        const facetForm = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        if (facetForm) {
          FacetFiltersForm.renderPage(searchParams, event);
        }
      }
    }
  }
  
  // Method to manually submit price filter (called by Apply button)
  submitPriceFilter() {
    const form = this.closest('form');
    if (form) {
      const formData = new FormData(form);
      // Preserve search query parameter for search pages
      const currentUrl = new URL(window.location);
      const searchQuery = currentUrl.searchParams.get('q');
      if (searchQuery) {
        formData.set('q', searchQuery);
        formData.set('options[prefix]', 'last');
      }
      // Reset to page 1 when price filter changes
      formData.delete('page');
      const searchParams = new URLSearchParams(formData).toString();
      const facetForm = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      if (facetForm) {
        FacetFiltersForm.renderPage(searchParams, null);
      }
    }
  }
  
  // Submit price filter if values changed from defaults
  submitIfChanged() {
    const currentMin = Number(this.minSlider?.value) || 0;
    const currentMax = Number(this.maxSlider?.value) || this.defaultMax;
    const isDefault = currentMin === this.defaultMin && currentMax === this.defaultMax;
    
    // Check if there's an active price filter in URL that needs to be cleared
    const currentUrl = new URL(window.location);
    const hasPriceFilterInUrl = currentUrl.searchParams.has('filter.v.price.gte') || 
                                 currentUrl.searchParams.has('filter.v.price.lte');
    
    if (!isDefault || hasPriceFilterInUrl) {
      this.hasUserInteracted = true;
      const form = this.closest('form');
      if (form) {
        const formData = new FormData(form);
        // Preserve search query parameter for search pages
        const searchQuery = currentUrl.searchParams.get('q');
        if (searchQuery) {
          formData.set('q', searchQuery);
          formData.set('options[prefix]', 'last');
        }
        // Reset to page 1 when price filter changes
        formData.delete('page');
        const searchParams = new URLSearchParams(formData).toString();
        FacetFiltersForm.renderPage(searchParams, null);
      }
    }
  }

  initDualRangeInput() {
    if (this.minSlider && this.maxSlider && typeof DualRangeInput !== 'undefined') {
      this.dualRangeInput = new DualRangeInput(this.minSlider, this.maxSlider);
      return true;
    } else {
      // Retry after a short delay if library not loaded yet
      if (typeof DualRangeInput === 'undefined') {
        setTimeout(() => this.initDualRangeInput(), 100);
      }
      return false;
    }
  }

  updateInputConstraints() {
    const minValue = Number(this.minSlider?.value);
    const maxValue = Number(this.maxSlider?.value);
    
    if (this.minInput && maxValue !== null) {
      this.minInput.setAttribute('max', maxValue);
    }
    if (this.maxInput && minValue !== null) {
      this.maxInput.setAttribute('min', minValue);
    }
  }

  updateRangeIndicator() {
    if (!this.rangeIndicator || !this.minSlider || !this.maxSlider || !this.track) {
      return;
    }
    
    const min = Number(this.minSlider.min);
    const max = Number(this.maxSlider.max);
    const minValue = Number(this.minSlider.value);
    const maxValue = Number(this.maxSlider.value);
    
    if (max === min) return;
    
    const trackWidth = this.track.offsetWidth;
    if (trackWidth === 0) {
      // Track not laid out yet, retry
      requestAnimationFrame(() => this.updateRangeIndicator());
      return;
    }
    
    const thumbWidth = 16; // Match --dri-thumb-width
    const thumbRadius = thumbWidth / 2;
    const leftExtension = 8; // Extend 8px to the left to cover track overlap
    
    // Calculate handle center positions
    // Range inputs position handles accounting for thumb width
    // Effective track is (trackWidth - thumbWidth)
    const effectiveWidth = trackWidth - thumbWidth;
    const minRatio = (minValue - min) / (max - min);
    const maxRatio = (maxValue - min) / (max - min);
    
    // Handle centers are at: thumbRadius + ratio * effectiveWidth
    const minHandleCenter = thumbRadius + minRatio * effectiveWidth;
    const maxHandleCenter = thumbRadius + maxRatio * effectiveWidth;
    
    // Range indicator goes from min handle center to max handle center
    // Extend 6px to the left to visually cover track overlap
    const leftPosition = minHandleCenter - leftExtension;
    const leftPercent = Math.max(0, (leftPosition / trackWidth) * 100);
    const widthPercent = Math.max(0, ((maxHandleCenter - leftPosition) / trackWidth) * 100);
    
    // Set CSS variables on both track and indicator for compatibility
    this.track.style.setProperty('--range-indicator-left', `${leftPercent}%`);
    this.track.style.setProperty('--range-indicator-width', `${widthPercent}%`);
    this.rangeIndicator.style.setProperty('--range-indicator-left', `${leftPercent}%`);
    this.rangeIndicator.style.setProperty('--range-indicator-width', `${widthPercent}%`);
    // Also set directly as fallback
    this.rangeIndicator.style.left = `${leftPercent}%`;
    this.rangeIndicator.style.width = `${widthPercent}%`;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    const anchor = this.querySelector('a');
    if (anchor) {
      anchor.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        if (form) {
          form.onActiveFilterClick(event);
        }
      });
      this.initialized = true;
    }
  }
}

customElements.define('facet-remove', FacetRemove);
